
// ===============================
// File: src/components/NewsFilters.js
// Purpose: Filters for News (mirrors PromotionsFilters.js but with NEWS enums)
// ===============================

import React from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import DropDown from './DropDown';
import active_on from './../images/icons/active_on.png';
import active_off from './../images/icons/active_off.png';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export default class NewsFilters extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      addNew: false,
      filtersCounter: 0,
      allowEdit: false,

      filters_isActive: 1,
      filters_type: 'ANY',
      filters_frequency: 'ANY',
      filters_datePreset: 'ANY',
      customFrom: '',
      customTo: '',

      typeOptions: {
        ANY: 'Any',
        0: 'Internal',
        1: 'External',
        2: 'Both',
        9: 'Undefined',
      },
      frequencyOptions: {
        ANY: 'Any',
        0: 'Once',
        1: 'Once a day',
        2: 'Once a week',
        3: 'Once a month',
      },
      datePresetOptions: {
        ANY:        'Any time',
        CUSTOM:     'Custom range',
        TODAY:      'Today',
        LAST_7:     'Last 7 days',
        LAST_30:    'Last 30 days',
        NEXT_7:     'Next 7 days',
        NEXT_30:    'Next 30 days',
        THIS_MONTH: 'This month',
        PREV_MONTH: 'Previous month',
        FUTURE:     'Upcoming only',
        PAST:       'Expired only',
      },
    };

    this.queryData = {};
    this.searchKeyword = React.createRef();

    this.handleExpandClick = this.handleExpandClick.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.searchKey = this.searchKey.bind(this);
    this.toggleActive = this.toggleActive.bind(this);
    this.resetFilters = this.resetFilters.bind(this);

    this.onDropType = this.onDropType.bind(this);
    this.onDropFrequency = this.onDropFrequency.bind(this);
    this.onDropDatePreset = this.onDropDatePreset.bind(this);
    this.onCustomFromChange = this.onCustomFromChange.bind(this);
    this.onCustomToChange = this.onCustomToChange.bind(this);
  }

  componentDidMount() {
    if (this.props.allowEdit === true) this.setState({ allowEdit: true });
    // Default: show active
    this.queryData.isActive = 1;
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const currentDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    this.setState({ customFrom: currentDate, customTo: currentDate });
    this.apply();
  }

  ymdToDmy = (s) => {
    if (!s || s.length < 10) return '';
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return '';
    return `${d}-${m}-${y}`;
  };
  endOfDayDmy = (s) => {
    const dmy = this.ymdToDmy(s);
    return dmy ? `${dmy} 23:59:59` : '';
  };
  ddmmyyyy(date) {
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  computeDateRange(preset) {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    const set = (from, to) => ({
      dateFrom_gte: from ? this.ddmmyyyy(from) : undefined,
      dateTo_lte:   to   ? this.ddmmyyyy(to)   : undefined,
    });
    switch (preset) {
      case 'TODAY':      return set(startOfToday, endOfToday);
      case 'LAST_7':     return set(new Date(startOfToday.getTime() - 6*24*3600*1000), endOfToday);
      case 'LAST_30':    return set(new Date(startOfToday.getTime() - 29*24*3600*1000), endOfToday);
      case 'NEXT_7':     return set(startOfToday, new Date(startOfToday.getTime() + 6*24*3600*1000));
      case 'NEXT_30':    return set(startOfToday, new Date(startOfToday.getTime() + 29*24*3600*1000));
      case 'THIS_MONTH': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end   = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
        return set(start, end);
      }
      case 'PREV_MONTH': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end   = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        return set(start, end);
      }
      case 'FUTURE':     return set(startOfToday, undefined);
      case 'PAST':       return set(undefined, endOfToday);
      default:           return set(undefined, undefined);
    }
  }

  handleExpandClick() { this.setState((s) => ({ expanded: !s.expanded })); }
  toggleAdd() {
    if (!this.state.allowEdit) return;
    this.setState((s) => ({ addNew: !s.addNew }));
    this.props.toggleAdd();
  }

  apply() {
    this.setState({ filtersCounter: Object.keys(this.queryData).length });
    this.props.applyFilters(this.queryData);
  }

  searchKey(e) {
    const keyword = e.target.value;
    if (keyword && keyword.length > 2) this.queryData.nameLike = keyword;
    else delete this.queryData.nameLike;
    this.apply();
  }

  toggleActive() {
    const newVal = this.state.filters_isActive ? 0 : 1;
    this.setState({ filters_isActive: newVal });
    if (newVal === 1) this.queryData.isActive = 1;
    else delete this.queryData.isActive;
    this.apply();
  }

  onDropType(v) {
    this.setState({ filters_type: v });
    if (v === 'ANY') delete this.queryData.type;
    else this.queryData.type = v;
    this.apply();
  }
  onDropFrequency(v) {
    this.setState({ filters_frequency: v });
    if (v === 'ANY') delete this.queryData.frequency;
    else this.queryData.frequency = v;
    this.apply();
  }

  onDropDatePreset(v) {
    this.setState({ filters_datePreset: v });
    delete this.queryData.dateFrom_gte;
    delete this.queryData.dateTo_lte;
    if (v === 'CUSTOM') { this.apply(); return; }
    if (v !== 'ANY') {
      const { dateFrom_gte, dateTo_lte } = this.computeDateRange(v);
      if (dateFrom_gte) this.queryData.dateFrom_gte = dateFrom_gte;
      if (dateTo_lte)   this.queryData.dateTo_lte   = dateTo_lte;
    }
    this.apply();
  }

  onCustomFromChange = (e) => {
    const ymd = e.target.value;
    this.setState({ customFrom: ymd });
    const dmy = this.ymdToDmy(ymd);
    if (dmy) this.queryData.dateFrom_gte = dmy; else delete this.queryData.dateFrom_gte;
    this.apply();
  };
  onCustomToChange = (e) => {
    const ymd = e.target.value;
    this.setState({ customTo: ymd });
    const dmyEnd = this.endOfDayDmy(ymd);
    if (dmyEnd) this.queryData.dateTo_lte = dmyEnd; else delete this.queryData.dateTo_lte;
    this.apply();
  };

  resetFilters() {
    this.queryData = {};
    if (this.searchKeyword.current) this.searchKeyword.current.value = '';
    this.setState({
      filters_isActive: 0, filtersCounter: 0,
      filters_type: 'ANY', filters_frequency: 'ANY',
      filters_datePreset: 'ANY',
    });
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const currentDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    this.setState({ customFrom: currentDate, customTo: currentDate });
    this.props.applyFilters(this.queryData);
  }

  render() {
    return (
      <Card sx={{ borderRadius: '16px', m: 1, backgroundColor: '#214a80' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton aria-label="Add News" title="Add News" onClick={this.toggleAdd}>
              <Avatar alt="Add News" sx={{ width: 24, height: 24, color: '#273442' }}>
                {this.state.addNew ? <FaMinusCircle /> : <FaPlusCircle />}
              </Avatar>
            </IconButton>

            <IconButton aria-label="Active" title="Active" onClick={() => this.toggleActive()}>
              <Avatar alt="Active" src={this.state.filters_isActive ? active_on : active_off} sx={{ width: 24, height: 24 }} />
            </IconButton>
          </Box>

          <TextField
            id="search-name"
            inputRef={this.searchKeyword}
            label="Title contains"
            variant="standard"
            onChange={this.searchKey}
            InputLabelProps={{ sx: { color: '#e3e3e5' } }}
            inputProps={{ sx: { color: '#e3e3e5' } }}
            sx={{ color: '#e3e3e5', marginLeft: '32px' }}
          />

          <DropDown
            name="Type"
            value={String(this.state.filters_type)}
            doptions={this.state.typeOptions}
            hasIcons={false}
            minWidth={100}
            onDark={false}
            onChangeFunction={this.onDropType}
            size="small"
            marginTop={8}
          />

          <DropDown
            name="Frequency"
            value={String(this.state.filters_frequency)}
            doptions={this.state.frequencyOptions}
            hasIcons={false}
            minWidth={100}
            onDark={false}
            onChangeFunction={this.onDropFrequency}
            size="small"
            marginTop={8}
            marginLeft={2}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <DropDown name="Date range"
                value={String(this.state.filters_datePreset)}
                doptions={this.state.datePresetOptions}
                hasIcons={false}
                minWidth={120}
                onDark={false}
                onChangeFunction={this.onDropDatePreset}
                size="small"
                marginTop={8}
                marginLeft={2}
              />

              {this.state.filters_datePreset === 'CUSTOM' && (
                <>
                  <TextField
                    id="date-custom-from"
                    label="From"
                    type="date"
                    variant="standard"
                    value={this.state.customFrom}
                    onChange={this.onCustomFromChange}
                    sx={{ color: '#e3e3e5', alignSelf: 'center', marginLeft: '16px' }}
                    InputLabelProps={{ shrink: true, sx: { color: '#e3e3e5' } }}
                    InputProps={{ sx: { color: '#e3e3e5' } }}
                  />
                  <TextField
                    id="date-custom-to"
                    label="To"
                    variant="standard"
                    type="date"
                    value={this.state.customTo}
                    onChange={this.onCustomToChange}
                    sx={{ color: '#e3e3e5', alignSelf: 'center', marginLeft: '16px' }}
                    InputLabelProps={{ shrink: true, sx: { color: '#e3e3e5' } }}
                    InputProps={{ sx: { color: '#e3e3e5' } }}
                  />
                </>
              )}
            </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#e3e3e5' }}>News: {this.props.counter}</Typography>
            <Typography variant="body2" sx={{ color: '#e3e3e5' }}>Filters: {this.state.filtersCounter}</Typography>
            {this.state.filtersCounter > 0 && (
              <IconButton aria-label="Remove Filters" title="Remove Filters" onClick={this.resetFilters}>
                <Avatar alt="Remove Filters" sx={{ width: 18, height: 18, color: '#99302e' }}>
                  <HighlightOffIcon fontSize="small" />
                </Avatar>
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ExpandMore expand={this.state.expanded} onClick={this.handleExpandClick}
              aria-expanded={this.state.expanded} aria-label="show more" sx={{ color: '#e3e3e5' }}>
              <ExpandMoreIcon />
            </ExpandMore>
          </Box>
        </Box>

        {/* Advanced: date presets + custom range */}
        {/* {this.state.expanded && (
          <CardContent>
          </CardContent>
        )} */}
      </Card>
    );
  }
}

